import { useLayoutEffect, useRef } from "react";

import type { RoadmapTriageEntry } from "@/lib/api";

import { returnTriageFocus } from "./triage";

/**
 * Where the focus goes as a dialog about a triage entry closes. The dialogs
 * have no trigger of their own, so this reads what had the focus as the
 * dialog opened (the row's action, or its phone menu), before the dialog's
 * first control takes it, and gives it back there, or to the row's menu, or,
 * the entry having left, to the group's toggle (`returnTriageFocus`). Returns
 * the dialog content's `onCloseAutoFocus`.
 */
export function useTriageReturnFocus(
  open: boolean,
  entry: Pick<RoadmapTriageEntry, "id"> | null,
): (event: Event) => void {
  const opener = useRef<HTMLElement | null>(null);
  // Read before the dialog mounts: its content may focus a control of its own as it does.
  useLayoutEffect(() => {
    if (open) {
      const active = document.activeElement;
      opener.current = active instanceof HTMLElement && active !== document.body ? active : null;
    }
  }, [open]);
  return (event) => {
    if (entry && returnTriageFocus(opener.current, entry.id)) {
      event.preventDefault();
    }
  };
}

interface TriageEntryTitleProps {
  entry: Pick<RoadmapTriageEntry, "title">;
}

/** The entry a dialog is about: its title in a light box, cut at four lines. */
export function TriageEntryTitle({ entry }: TriageEntryTitleProps) {
  return (
    <p className="bg-accent-light/40 text-foreground line-clamp-4 rounded-md border px-3 py-2 text-sm wrap-anywhere">
      {entry.title}
    </p>
  );
}
